import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Moon,
  Sun,
  Bell,
  Search,
  Menu,
  LogOut,
  Shield,
  KeyRound,
} from "lucide-react";
import { useStaffMe } from "../../context/StaffMeContext";
import { useUIStore } from "../../stores/uiStore";
import { UserAvatar } from "../common/UserAvatar";
import { api } from "../../api/staff";

export function Topbar() {
  const me = useStaffMe();
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode, toggleMobileMenu } = useUIStore();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const displayName = me.displayName || me.username || "Staff";

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await api<void>("/staff/auth/logout", { method: "POST" });
    } catch {
      // Ignored
    }
    navigate("/login", { replace: true });
  }

  return (
    <header className="h-16 bg-card border-b border-border flex items-center justify-between px-4 md:px-6 gap-4 shrink-0 select-none">
      {/* Left controls */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggleMobileMenu}
          className="md:hidden p-2 rounded-lg text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Search button */}
        <button
          type="button"
          onClick={() => navigate("/users")}
          className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 border border-border rounded-lg text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors max-w-xs w-64"
        >
          <Search className="w-4 h-4 shrink-0" />
          <span className="flex-1 text-left truncate">Rechercher un utilisateur…</span>
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] bg-background border border-border rounded text-muted-foreground font-mono">
            /users
          </kbd>
        </button>
      </div>

      {/* Right controls */}
      <div className="flex items-center gap-2">
        {/* Dark Mode toggle */}
        <button
          type="button"
          onClick={toggleDarkMode}
          className="p-2 rounded-lg text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          title={darkMode ? "Mode Clair" : "Mode Sombre"}
        >
          {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>

        {/* User profile menu */}
        <div ref={userMenuRef} className="relative">
          <button
            type="button"
            onClick={() => setUserMenuOpen((v) => !v)}
            className="flex items-center gap-2.5 p-1.5 rounded-lg hover:bg-accent transition-colors"
          >
            <UserAvatar user={{ displayName, avatarUrl: me.avatarUrl }} size="sm" />
            <div className="hidden sm:flex flex-col text-left">
              <span className="text-xs font-semibold text-foreground leading-tight">
                {displayName}
              </span>
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                {me.role}
              </span>
            </div>
          </button>

          {/* User Menu Dropdown */}
          {userMenuOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 rounded-xl bg-card border border-border shadow-lg p-1.5 z-50">
              <div className="px-3 py-2 border-b border-border mb-1">
                <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
                {me.username && (
                  <p className="text-xs text-muted-foreground font-mono truncate">@{me.username}</p>
                )}
                <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-primary/10 text-primary">
                  <Shield className="w-3 h-3" />
                  {me.role}
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate("/roles");
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg text-foreground hover:bg-accent transition-colors"
              >
                <Shield className="w-3.5 h-3.5 text-muted-foreground" />
                Matrice des Rôles (RBAC)
              </button>

              <button
                type="button"
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate("/passkeys");
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg text-foreground hover:bg-accent transition-colors"
              >
                <KeyRound className="w-3.5 h-3.5 text-muted-foreground" />
                Mes Clés de Sécurité
              </button>

              <div className="my-1 border-t border-border" />

              <button
                type="button"
                disabled={loggingOut}
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" />
                {loggingOut ? "Déconnexion…" : "Se déconnecter"}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
